import React, { useState, useEffect, useMemo } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Add from "@mui/icons-material/Add";
import {
    CrmPage,
    CrmPageHeader,
    CrmActionButton,
    CrmTableCard,
    CrmNotice,
    CrmFilterBar,
    CrmCard,
    CrmSectionHeader,
    CrmRowActions,
    ListLink,
    ListStatus,
    ListTruncate,
} from "../shared/CrmUi";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import Footer from "../../../../../../examples/Footer";
import { format, addHours, differenceInHours, differenceInMinutes } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appConfirm } from "components/AppDialog";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

function normalizeSalesRepIds(salesRep) {
    if (!salesRep) return [];
    return (Array.isArray(salesRep) ? salesRep : [salesRep])
        .map((v) => {
            if (!v) return null;
            if (typeof v === "string") return v;
            if (typeof v === "object") {
                if (v.id) return String(v.id);     // populate id
                if (v._id) return String(v._id);   // raw _id
                if (typeof v.toString === "function") return String(v.toString()); // ObjectId
            }
            return null;
        })
        .filter(Boolean);
}
function buildFullMeetingDate(meeting) {
    const meetingDate = meeting?.meetingDate ? new Date(meeting.meetingDate) : null;
    if (!meetingDate || Number.isNaN(meetingDate.getTime())) return null;
    const [hour, minute] = (meeting.meetingTime || "00:00").split(":").map(Number);
    return new Date(
        meetingDate.getFullYear(),
        meetingDate.getMonth(),
        meetingDate.getDate(),
        hour || 0,
        minute || 0
    );
}

function MeetingTable() {
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [salesRepOptions, setSalesRepOptions] = useState([]);
    const [selectedSalesReps, setSelectedSalesReps] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await CrudService.getMeetings();
                const normalizedData = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalizedData);
            } catch (error) {
                console.error("Error fetching meetings:", error);
                setNotification({
                    value: true,
                    text: "Failed to load meetings. Please try again later.",
                });
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await CrudService.getUsers();
                const users = res.data || [];
                setSalesRepOptions(
                    users.map((u) => ({
                        id: u.id,
                        label: u.attributes?.name || "Unnamed",
                    }))
                );
            } catch (e) {
                console.error("Error fetching users:", e);
            }
        };
        fetchUsers();
    }, []);

    const selectedRepIds = useMemo(
            () => selectedSalesReps.map((r) => r.id),
            [selectedSalesReps]
        );
    const { min: minDate, max: maxDate } = useMemo(() => {
        const toStart = (d) => d ? new Date(d.startOf("day").toISOString()) : null;
        const toEnd   = (d) => d ? new Date(d.endOf("day").toISOString())   : null;
        return { min: toStart(startDate), max: toEnd(endDate) };
    }, [startDate, endDate]);

    const filteredData = useMemo(() => {
        return data.filter((meeting) => {
            if (minDate || maxDate) {
                const onlyDate = meeting?.meetingDate ? new Date(meeting.meetingDate) : null;
                if (!onlyDate) return false;
                if (minDate && onlyDate < minDate) return false;
                if (maxDate && onlyDate > maxDate) return false;
            }
            if (selectedRepIds.length > 0) {
                // salesRep: [{ type: ObjectId, ref: "User" }] -> normalize
                const ids = normalizeSalesRepIds(meeting.salesRep); // ["id","id",...]
                const intersects = ids.some((id) => selectedRepIds.includes(id));
                if (!intersects) return false;
            }
            return true;
        });
    }, [data, minDate, maxDate, selectedRepIds]);


    const getRows = (rowsData) => {
        return rowsData.map((meeting) => {
            const fullMeetingTime = buildFullMeetingDate(meeting);
            const meetingDateTimeStr = fullMeetingTime ? format(fullMeetingTime, "dd-MM-yyyy HH:mm") : "-";

            const meetingEndTime = fullMeetingTime ? addHours(fullMeetingTime, 24) : null;
            let timeLeft = <MDTypography sx={{ fontSize: 12, color: "#667085" }}>—</MDTypography>;

            if (meeting.meetingStatus === "Completed") {
                if (meeting.meetingNotes && meeting.meetingNotes.trim() !== "") {
                    timeLeft = <ListStatus label="Meeting closed" tone="green" />;
                } else if (meetingEndTime) {
                    const hoursLeft = differenceInHours(meetingEndTime, new Date());
                    const minutesLeft = differenceInMinutes(meetingEndTime, new Date()) % 60;

                    if (hoursLeft >= 0 || minutesLeft > 0) {
                        timeLeft = <ListStatus label={`${hoursLeft}h ${minutesLeft}m left`} tone="amber" />;
                    } else {
                        timeLeft = <ListStatus label="Meeting note missing" tone="red" />;
                    }
                }
            }

            return {
                id: meeting.id,
                customer: (
                    <ListLink onClick={() => clickEditHandler(meeting.id)}>
                        <ListTruncate text={meeting.customer || "—"} max={40} />
                    </ListLink>
                ),
                representatives: Array.isArray(meeting.representatives)
                    ? meeting.representatives.map((u) => u?.name || "").join(", ")
                    : meeting.representatives?.name || meeting.representatives || "-",
                customerCountry: meeting.customerCountry,
                city: meeting.city,
                meetingDateTime: meetingDateTimeStr,
                meetingStatus: (
                    <ListStatus
                        label={meeting.meetingStatus}
                        tone={meeting.meetingStatus === "Completed" ? "green" : meeting.meetingStatus === "Cancelled" ? "red" : "blue"}
                    />
                ),
                timeLeft,
                actions: (
                    <CrmRowActions
                        onEdit={() => clickEditHandler(meeting.id)}
                        onDelete={(e) => clickDeleteHandler(e, meeting.id)}
                        editTitle="Edit meeting"
                        deleteTitle="Delete meeting"
                    />
                ),
            };
        });
    };

    useEffect(() => {
        setTableData(getRows(filteredData));
    }, [filteredData, selectedRows]);

    const clickEditHandler = (id) => {
        navigate(`/meeting/create-meeting/details?id=${id}`);
    };

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this meeting?")) return;

            await CrudService.deleteMeeting(id);
            const updatedData = data.filter((item) => item.id !== id);
            setData(updatedData);

            setNotification({ value: true, text: "Meeting deleted successfully." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        } catch (error) {
            console.error("Error deleting meeting:", error);
            setNotification({ value: true, text: "Failed to delete meeting. Please try again later." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };

    const calendarEvents = useMemo(() => {
        return filteredData.map((meeting) => {
            const fullMeetingTime = buildFullMeetingDate(meeting);
            const iso = fullMeetingTime ? fullMeetingTime.toISOString() : new Date().toISOString();
            return { title: meeting.customer || "Unnamed", date: iso, id: String(meeting.id) };
        });
    }, [filteredData]);

    const dataTableData = {
        columns: [
            { Header: "Customer", accessor: "customer" },
            { Header: "Representatives", accessor: "representatives" },
            { Header: "Country", accessor: "customerCountry" },
            { Header: "City", accessor: "city" },
            { Header: "Meeting Date & Time", accessor: "meetingDateTime" },
            { Header: "Meeting Status", accessor: "meetingStatus" },
            { Header: "Time Left", accessor: "timeLeft" },
            { Header: "Actions", accessor: "actions", disableSortBy: true },
        ],
        rows: tableData,
    };

    const clickAddHandler = () => {
        navigate("/meeting/create-meeting/details");
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Meetings"
                    subtitle="Scheduled customer meetings by date and sales representative, with a monthly calendar view."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            Create Meeting
                        </CrmActionButton>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <CrmFilterBar>
                                <DatePicker
                                    label="Start Date"
                                    format="DD.MM.YYYY"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    slotProps={{ textField: { variant: "outlined", size: "small", sx: { minWidth: 200 } } }}
                                />

                                <DatePicker
                                    label="End Date"
                                    format="DD.MM.YYYY"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    slotProps={{ textField: { variant: "outlined", size: "small", sx: { minWidth: 200 } } }}
                                />

                                {/* SalesRep Çoklu Filtre */}
                                <Autocomplete
                                    multiple
                                    options={salesRepOptions}
                                    value={selectedSalesReps}
                                    onChange={(e, newVal) => setSelectedSalesReps(newVal)}
                                    getOptionLabel={(opt) => opt.label}
                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                    sx={{ minWidth: 260 }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Filter by SalesRep" placeholder="Select reps" size="small" />
                                    )}
                                />

                                <CrmActionButton
                                    variant="secondary"
                                    onClick={() => {
                                        setStartDate(null);
                                        setEndDate(null);
                                        setSelectedSalesReps([]);
                                    }}
                                    disabled={(!startDate && !endDate) && selectedSalesReps.length === 0}
                                >
                                    Clear Filters
                                </CrmActionButton>
                            </CrmFilterBar>
                        </LocalizationProvider>

                <CrmTableCard title="Meeting List" summary={`${tableData.length} meetings`}>
                    <DataTable table={dataTableData} canSearch />
                </CrmTableCard>

            <CrmCard>
                <MDBox p={2.5}>
                <CrmSectionHeader title="Meeting Calendar" subtitle="Click a meeting to open it, or a day to schedule a new one." />
                <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    firstDay={1}
                    events={calendarEvents}
                    height="auto"
                    eventClick={(info) => navigate(`/meeting/create-meeting/details?id=${info.event.id}`)}
                    dateClick={(info) => navigate(`/meeting/create-meeting/details?date=${info.dateStr}`)}
                    dayCellDidMount={(arg) => {
                        const day = arg.date.getDay();
                        if (day === 0) arg.el.style.backgroundColor = "#FFF7ED";    // Pazar
                        else if (day === 6) arg.el.style.backgroundColor = "#F5F8FF"; // Cumartesi
                    }}
                />
                </MDBox>
            </CrmCard>
            </CrmPage>
            <Footer />
        </DashboardLayout>
    );
}

export default MeetingTable;
